import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { 
  FaFacebookF, 
  FaTwitter, 
  FaInstagram, 
  FaYoutube, 
  FaEnvelope, 
  FaPhone, 
  FaMapMarkerAlt,
  FaCheckCircle,
  FaExclamationCircle
} from 'react-icons/fa';
import { subscribeToNewsletter } from '../firebase/firestoreService';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    
    // Clear previous messages
    setMessage({ type: '', text: '' });
    
    // Validate email
    if (!email.trim()) {
      setMessage({ type: 'error', text: 'Please enter your email address.' });
      return;
    }
    
    if (!validateEmail(email)) {
      setMessage({ type: 'error', text: 'Please enter a valid email address.' });
      return;
    }
    
    setLoading(true);
    
    try {
      const result = await subscribeToNewsletter(email);
      
      if (result.success) {
        setMessage({ type: 'success', text: 'Thank you for subscribing to our newsletter!' });
        setEmail('');
      } else if (result.error) {
        setMessage({ type: 'error', text: result.error });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'An error occurred. Please try again later.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <FooterContainer>
      <FooterContent>
        <FooterSection>
          <FooterLogo>
            <span>Master</span> Collectors Forge
          </FooterLogo>
          <FooterDescription>
            Your premier destination for high-quality collectibles. We specialize in rare coins, 
            commemorative rounds, investment-grade bullion, and exclusive merchandise.
          </FooterDescription>
          <SocialLinks>
            <SocialLink href="https://facebook.com" target="_blank" rel="noopener noreferrer">
              <FaFacebookF />
            </SocialLink>
            <SocialLink href="https://twitter.com" target="_blank" rel="noopener noreferrer">
              <FaTwitter />
            </SocialLink>
            <SocialLink href="https://instagram.com" target="_blank" rel="noopener noreferrer">
              <FaInstagram />
            </SocialLink>
            <SocialLink href="https://youtube.com" target="_blank" rel="noopener noreferrer">
              <FaYoutube />
            </SocialLink>
          </SocialLinks>
        </FooterSection>

        <FooterSection>
          <FooterHeading>Quick Links</FooterHeading>
          <FooterLinks>
            <FooterLinkItem>
              <FooterLink to="/">Home</FooterLink>
            </FooterLinkItem>
            <FooterLinkItem>
              <FooterLink to="/categories">Categories</FooterLink>
            </FooterLinkItem>
            <FooterLinkItem>
              <FooterLink to="/about">About Us</FooterLink>
            </FooterLinkItem>
            <FooterLinkItem>
              <FooterLink to="/contact">Contact</FooterLink>
            </FooterLinkItem>
            <FooterLinkItem>
              <FooterLink to="/legal-policies">Legal Policies</FooterLink>
            </FooterLinkItem>
          </FooterLinks>
        </FooterSection>

        <FooterSection>
          <FooterHeading>Categories</FooterHeading>
          <FooterLinks>
            <FooterLinkItem>
              <FooterLink to="/categories">All Products</FooterLink>
            </FooterLinkItem>
            <FooterLinkItem>
              <FooterLink to="/categories?category=Silver">Silver</FooterLink>
            </FooterLinkItem>
            <FooterLinkItem>
              <FooterLink to="/categories?category=Copper">Copper</FooterLink>
            </FooterLinkItem>
            <FooterLinkItem>
              <FooterLink to="/categories?category=Ancient/Shipwreck">Ancient/Shipwreck</FooterLink>
            </FooterLinkItem>
            <FooterLinkItem>
              <FooterLink to="/treasure-hunt">Treasure Hunt</FooterLink>
            </FooterLinkItem>
          </FooterLinks>
        </FooterSection>

        <FooterSection>
          <FooterHeading>Contact Us</FooterHeading>
          <ContactInfo>
            <ContactItem>
              <ContactIcon>
                <FaEnvelope />
              </ContactIcon>
              <ContactText>info@mastercollectorsforge.com</ContactText>
            </ContactItem>
            <ContactItem>
              <ContactIcon>
                <FaPhone />
              </ContactIcon>
              <ContactText>505-363-6058</ContactText>
            </ContactItem>
            <ContactItem>
              <ContactIcon>
                <FaMapMarkerAlt />
              </ContactIcon>
              <ContactText>Belen, New Mexico</ContactText>
            </ContactItem>
          </ContactInfo>
          <NewsletterForm onSubmit={handleSubscribe}>
            <NewsletterInput 
              type="email" 
              placeholder="Your email address" 
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
            />
            <NewsletterButton type="submit" disabled={loading}>
              {loading ? 'Subscribing...' : 'Subscribe'}
            </NewsletterButton>
          </NewsletterForm>
          {message.text && (
            <NewsletterMessage $type={message.type}>
              {message.type === 'success' ? <FaCheckCircle /> : <FaExclamationCircle />}
              <span>{message.text}</span>
            </NewsletterMessage>
          )}
        </FooterSection>
      </FooterContent>

      <FooterBottom>
        <CopyrightText>
          &copy; {currentYear} Master Collectors Forge. All Rights Reserved.
        </CopyrightText>
        <FooterLegalLinks>
          <FooterLegalLink to="/legal-policies?policy=returns">Returns</FooterLegalLink>
          <FooterLegalLink to="/legal-policies?policy=refunds">Refunds</FooterLegalLink>
          <FooterLegalLink to="/legal-policies?policy=cancellations">Cancellations</FooterLegalLink>
          <FooterLegalLink to="/legal-policies?policy=restrictions">Legal Restrictions</FooterLegalLink>
          <FooterLegalLink to="/legal-policies?policy=promotions">Promotion Terms</FooterLegalLink>
        </FooterLegalLinks>
      </FooterBottom>
    </FooterContainer>
  );
};

// Styled Components
const FooterContainer = styled.footer`
  background: ${({ theme }) => theme.gradients.darkBlue};
  color: ${({ theme }) => theme.colors.text.primary};
  padding: 4rem 0 1.5rem;
  border-top: 2px solid ${({ theme }) => theme.colors.metallic.gold};
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
`;

const FooterSection = styled.div`
  margin-bottom: 2rem;
`;

const FooterLogo = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: ${({ theme }) => theme.colors.text.primary};
  
  span {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const FooterDescription = styled.p`
  font-size: 0.9rem;
  line-height: 1.6;
  margin-bottom: 1.5rem;
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.background.medium};
  color: ${({ theme }) => theme.colors.text.primary};
  transition: all ${({ theme }) => theme.transitions.medium};
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.primary};
    transform: translateY(-3px);
  }
`;

const FooterHeading = styled.h4`
  font-size: 1.2rem;
  margin-bottom: 1.5rem;
  color: ${({ theme }) => theme.colors.text.primary};
  position: relative;
  padding-bottom: 0.5rem;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 50px;
    height: 2px;
    background-color: ${({ theme }) => theme.colors.primary};
  }
`;

const FooterLinks = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const FooterLinkItem = styled.li``;

const FooterLink = styled(Link)`
  color: ${({ theme }) => theme.colors.text.secondary};
  transition: color ${({ theme }) => theme.transitions.short};
  position: relative;
  padding-left: 0.75rem;
  
  &::before {
    content: '›';
    position: absolute;
    left: 0;
    color: ${({ theme }) => theme.colors.primary};
  }
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
`;

const ContactIcon = styled.div`
  margin-right: 0.75rem;
  color: ${({ theme }) => theme.colors.primary};
`;

const ContactText = styled.span`
  font-size: 0.9rem;
`;

const NewsletterForm = styled.form`
  display: flex;
  margin-top: 1rem;
`;

const NewsletterInput = styled.input`
  flex: 1;
  padding: 0.75rem;
  border: none;
  border-radius: 4px 0 0 4px;
  background-color: ${({ theme }) => theme.colors.background.medium};
  color: ${({ theme }) => theme.colors.text.primary};
  
  &::placeholder {
    color: ${({ theme }) => theme.colors.text.muted};
  }
  
  &:focus {
    outline: none;
  }
`;

const NewsletterButton = styled.button`
  padding: 0.75rem 1rem;
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.text.primary};
  border-radius: 0 4px 4px 0;
  transition: background-color ${({ theme }) => theme.transitions.short};
  
  &:hover:not(:disabled) {
    background-color: ${({ theme }) => theme.colors.secondary};
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const NewsletterMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.75rem;
  padding: 0.75rem;
  border-radius: 4px;
  font-size: 0.9rem;
  background-color: ${({ $type, theme }) => 
    $type === 'success' 
      ? 'rgba(74, 222, 128, 0.1)' 
      : 'rgba(248, 113, 113, 0.1)'};
  color: ${({ $type }) => 
    $type === 'success' 
      ? '#4ade80' 
      : '#f87171'};
  
  svg {
    flex-shrink: 0;
  }
  
  span {
    flex: 1;
  }
`;

const FooterBottom = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 1.5rem;
  border-top: 1px solid ${({ theme }) => theme.colors.background.medium};
  text-align: center;
  margin-top: 2rem;
`;

const CopyrightText = styled.p`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const FooterLegalLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 1rem;
  margin-top: 1rem;
`;

const FooterLegalLink = styled(Link)`
  color: ${({ theme }) => theme.colors.text.muted};
  transition: color ${({ theme }) => theme.transitions.short};
  
  &:hover {
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

export default Footer;
